// '2' is In Progress. It was absent here, from the status map and from the backend's TicketState,
// while 100 rows on Dev already carried it (TDF-1449).
export type TicketStatus = '1' | '2' | '3' | '6' | '10' | '18';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

/**
 * TDF-1341/1394: stable status key the backend now returns alongside `state_label`
 * (`SupportTicketStatus` on the backend/BFF). Supersedes keying ticket-state UI logic off
 * the legacy numeric `status`/`state` fields, which are `null` for IT Services/Oracle ERP
 * Issues tickets — those two topics only ever populate `status_key`/`state_label`.
 */
export const SUPPORT_TICKET_STATUS_KEYS = [
  'NEW',
  'OPEN',
  'AWAITING_CUSTOMER',
  'IN_PROGRESS',
  'ON_HOLD',
  'AWAITING_3RD_PARTY_INFO',
  'RESOLVED',
  'CLOSED',
  'CANCELED',
] as const;

export type SupportTicketStatusKey =
  (typeof SUPPORT_TICKET_STATUS_KEYS)[number];

export interface SupportTicket {
  ticket_id: string;
  ticket_number: string;
  // Raw ticket topic (matches TicketCategory: "General", "Service Specific",
  // "Oracle ERP Issues", etc.) — backs the list "Topic" column (Figma redesign).
  topic?: string;
  subject: string;
  preview: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: string;
  active: boolean;
  state?: string; // Ticket state: 6 = resolved (show respond button), 10 = in-progress (show chat input)
  functionName: string; // TDF-133: Function name for closed tickets tab
  createdDate: string;
  created_at: string;
  closureDate?: string; // TDF-133: Closure date for closed tickets
  closed_at?: string;
  lastUpdated: string;
  createdBy: string;
  assignedTo?: string;
  isUnread?: boolean;
  comments?: unknown;
  has_unread?: boolean;
  attachments?: unknown[];
  service_l3?: string;
  sr_attempt_id?: string | null;
  ritm_number?: string | null;
  service_name?: string | null;
  created_by_name?: string | null;
  // TDF-1342 (added 2026-09-09) — populated on a bundle ticket, mutually
  // exclusive with sr_attempt_id.
  bundle_attempt_id?: string | null;
  bundle_id?: string | null;
  bundle_name?: string | null;
  // TDF-1388: client-facing Sub-Topic — populated for Oracle ERP Issues tickets only.
  // The list/detail "Service/sub-topic" column falls back to this when service_l3 is absent.
  sub_topic?: string | null;
  // TDF-1394: raw ServiceNow state label (New/Open/Awaiting Customer/Resolved/Closed) —
  // render as-is, never translated, never derived from the numeric `state`/TicketState.
  state_label?: string | null;
  // TDF-1341/1394: stable status key alongside state_label — null for a state ServiceNow
  // hasn't introduced to us yet. Drives StatusBadge color/icon; state_label stays the
  // verbatim display text (AC8).
  status_key?: SupportTicketStatusKey | null;
}

export interface TicketFilters {
  status?: TicketStatus;
  priority?: TicketPriority;
  category?: string;
  searchQuery?: string;
}

/**
 * Support Tickets API types
 */
export interface TicketsListResponse {
  tickets: SupportTicket[];
  current_page: number;
  has_next: boolean;
  has_previous: boolean;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface TicketDetailResponse extends SupportTicket {
  description?: string;
  attachments?: Array<{
    id: string;
    name: string;
    url: string;
    size: number;
    uploadedAt: string;
  }>;
  history?: Array<{
    id: string;
    action: string;
    timestamp: string;
    performedBy: string;
  }>;
}

export interface CreateTicketRequest {
  topic: TopicCategory;
  subject: string;
  functionL1: string;
  typeL2: string;
  serviceL3: string;
  description: string;
  attachment_metadata?: {
    fileId: string;
    fileName: string;
    uploadedBy: string;
    fileType: string;
    gcsUri: string;
    fileSizeBytes: number;
  }[];
  serviceId?: string | undefined;
  srAttemptId?: string | undefined;
  // TDF-1342 (added 2026-09-09) — only valid when topic is "Requested Service";
  // exactly one of srAttemptId/bundleAttemptId, never both, never neither.
  bundleAttemptId?: string | undefined;
  parentSrId?: string | undefined;
  // TDF-1388: mandatory together when topic === ORACLE_ERP_ISSUES, absent otherwise.
  subTopic?: string | undefined;
  issueKey?: string | undefined;
}

export interface CreateTicketResponse {
  ticketId: string;
  ticketNumber: string;
  createdAt: string;
  status: string;
}

export interface UploadAttachmentsRequest {
  file: File;
}

export interface UploadAttachmentsResponse {
  fileId: string;
  originalFilename: string;
  gcsUri: string;
  uploadedAt: string;
  uploadedBy: string;
  mimeType: string;
  fileSizeBytes: number;
  attachments: Array<{
    fileId: string;
    originalFilename: string;
    gcsUri: string;
    size: number;
    uploadedAt: string;
    uploadedBy: string;
    mimeType: string;
    fileSizeBytes: number;
  }>;
}
// Request type that includes ticketId for hook usage
export interface AddCommentRequest {
  ticketId: string;
  formData: FormData;
}

export interface AddCommentResponse {
  commentId: string;
  ticketId: string;
  comment: string;
  createdAt: string;
  createdBy: string;
  attachments?: Array<{
    id: string;
    name: string;
    url: string;
    size: number;
  }>;
}

export interface TicketFiltersParams {
  status?: 'open' | 'closed';
  category?: string;
  searchQuery?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Support Tickets API Endpoints
 * Centralized endpoint configuration for support tickets operations
 */
export const SUPPORT_TICKETS_ENDPOINTS = {
  BASE: 'support-ticketing',
  OPEN: 'support-ticketing/open-tickets',
  CLOSED: 'support-ticketing/closed-tickets',
  CREATE: 'support-ticketing/tickets',
  DETAIL: (ticketId: string) => `support-ticketing/tickets/detail/${ticketId}`,
  UPLOAD_ATTACHMENTS: () => `support-ticketing/tickets/attachments`,
  SEARCH: 'support-ticketing/tickets/search',
  ADD_COMMENT: (ticketId: string) =>
    `support-ticketing/tickets/${ticketId}/comments`,
  DROPDOWN_OPTIONS: 'support-ticketing/dropdown-options',
  // TDF-1388/TDF-1390: Topic → Sub-Topic → Issue catalog
  SUPPORT_ISSUES: 'support-ticketing/support-issues',
} as const;

export enum TopicCategory {
  // eslint-disable-next-line no-unused-vars
  GENERAL = 'General',
  // eslint-disable-next-line no-unused-vars
  SERVICE_SPECIFIC = 'Service Specific',
  // eslint-disable-next-line no-unused-vars
  ACTIVE_SERVICES = 'Active Services',
  // eslint-disable-next-line no-unused-vars
  REQUESTED_SERVICES = 'Requested Service',
  // eslint-disable-next-line no-unused-vars
  IT_SERVICES = 'IT Services',
  // eslint-disable-next-line no-unused-vars
  ORACLE_ERP_ISSUES = 'Oracle ERP Issues',
}

/**
 * TDF-1388/TDF-1390: Topic → Sub-Topic → Issue catalog, feeds the Sub-Topic/Issue
 * cascade under Oracle ERP Issues and the single item under IT Services. Each issue
 * carries the ServiceNow producerSysId used to fetch its dynamic form schema
 * (GET catalog-items/:sysId/schema, reused as-is per TDF-1390 dev notes).
 */
export interface SupportIssueCatalogItem {
  issueKey: string;
  name: string;
  descriptionPlaceholder?: string | null;
  producerSysId: string;
  targetTable: string;
}

export interface SupportSubTopicCatalog {
  subTopic: string | null;
  issues: SupportIssueCatalogItem[];
}

export interface SupportTopicCatalog {
  topic: string;
  subTopics: SupportSubTopicCatalog[];
}

export interface SupportIssuesCatalogResponse {
  topics: SupportTopicCatalog[];
}

// Nested dropdown structure from backend
export interface DropdownOffering {
  value: string;
  label: string;
  sysId?: string;
}

export interface DropdownServiceType {
  value: string;
  label: string;
  sysId?: string;
  offerings: DropdownOffering[];
}

export interface DropdownFunction {
  value: string;
  label: string;
  sysId?: string;
  serviceTypes: DropdownServiceType[];
}

export type DropdownOptionsResponse = DropdownFunction[];
